import {
	ColDef,
	PostSortRowsParams,
	RowClickedEvent,
	ValueGetterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
	useCallback,
	useMemo,
	useRef,
} from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import useUiuxStore from 'src/store/useUiuxStore';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	getRowStyleFlags,
} from 'src/utils/grid/gridStyleUtils';
import {
	createCellCssClassRules,
	CreateCellDoubleClickHandler,
	CustomCellDoubleClickAction,
	GridCellConfig,
	InsertRowHandler,
	postSortHandler,
	RemoveRowHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import {
	calcBalance,
	MST2020_R01Dto,
} from './Dto_MST2020_R01';
import { useMST2020_R01CellChange } from './hooks/MST2020_R01CellChange';
import { useMST2020_R01Store } from './Store_MST2020_R01';

const COLUMN_STATE_KEY =
	'MST2020_R01-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['customNm', 'balanceAmt'],
	insertedDisabledCells: [''],
	commonDisabledCells: ['customNm', 'balanceAmt'],
	pointerCells: [
		'customCd',
		'purchaseAmt',
		'salesAmt',
		'receiptAmt',
		'paymentAmt',
		'remark',
	],
	insertedPointerCells: ['customCd'],
	commonPointerCells: [''],
	alignLeftCells: ['customCd', 'customNm', 'remark'],
};

const numberFormatter = (p: { value: unknown }) =>
	p.value == null || p.value === ''
		? '0'
		: Number(p.value).toLocaleString();

const numberParser = (p: { newValue: unknown }) => {
	const n = Number(
		String(p.newValue ?? '').replace(/,/g, ''),
	);

	return Number.isNaN(n) ? 0 : n;
};

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<
		AgGridReact<MST2020_R01Dto> | null
	>;
	defaultMST2020_R01Row: MST2020_R01Dto;
}

export const Table_MST2020_R01 = ({
	clickable,
	setClickable,
	gridRef,
	defaultMST2020_R01Row,
}: Props) => {
	const {
		MST2020_R01List,
		setMST2020_R01List,
		insertMST2020_R01List,
		removeMST2020_R01List,
		updateMST2020_R01List,
		deletedMST2020_R01List,
		addDeletedMST2020_R01List,
		selectedMST2020_R01List,
		setSelectedMST2020_R01List,
		initSelectedMST2020_R01List,
		allSelectedMST2020_R01List,
		originalMST2020_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useMST2020_R01Store();

	const { setCodeSearchModalProps } = useUiuxStore();
	const gridWrapperRef =
		useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: MST2020_R01List,
		defaultGridData: defaultMST2020_R01Row,
		selectedRow: selectedMST2020_R01List,
		originalGridData: originalMST2020_R01List,
		updateRow: (index, data) =>
			updateMST2020_R01List(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		customCd: '거래처코드',
		customNm: '거래처명',
		purchaseAmt: '매입액',
		salesAmt: '매출액',
		receiptAmt: '수금액',
		paymentAmt: '지불액',
		balanceAmt: '잔액',
		remark: '비고',
	};

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules =
			createCellClassRules<MST2020_R01Dto>({
				idKey: 'valueIndex',
				selectedList: selectedMST2020_R01List,
				deletedList: deletedMST2020_R01List,
				disabledInsertedColumns,
				disabledColumns,
				pointerInsertedColumns,
				pointerColumns,
				alignLeftColumns,
			});

		return {
			...defaultColDef,
			cellClassRules: {
				...baseRules,
			},
		};
	}, [
		selectedMST2020_R01List,
		deletedMST2020_R01List,
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	]);

	const defaultEditableColumns = [
		'customCd',
		'purchaseAmt',
		'salesAmt',
		'receiptAmt',
		'paymentAmt',
		'remark',
	];

	const detailTableHandlers =
		useMST2020_R01CellChange({
			MST2020_R01List,
			originalMST2020_R01List,
			updateMST2020_R01List,
			gridRef,
			setCodeSearchModalProps,
		});

	const cellEventHandlers = useDetailTableEvents({
		detailList: MST2020_R01List,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		},
	});

	const insertRowHandler = useCallback(
		(
			rowIndex: number,
			e: React.MouseEvent,
		) => {
			InsertRowHandler({
				event: e,
				dataList: MST2020_R01List,
				rowData: MST2020_R01List[rowIndex],
				insertRow: insertMST2020_R01List,
				defaultRowData:
					defaultMST2020_R01Row,
				setClickable,
			});
		},
		[
			MST2020_R01List,
			insertMST2020_R01List,
			defaultMST2020_R01Row,
			setClickable,
		],
	);

	const baseColumnDefs: ColDef<MST2020_R01Dto>[] =
		useMemo(
			() => [
				...getDefaultRowIndexColumnConfig(
					(params) =>
						renderIndexCell(
							params,
							(inner) =>
								getRowStyleFlags(
									inner,
									{
										deletedList:
											deletedMST2020_R01List,
										selectedList:
											selectedMST2020_R01List,
										dataList:
											MST2020_R01List,
									},
								),
							(rowIndex, e) =>
								insertRowHandler(
									rowIndex,
									e,
								),
							(rowData) =>
								RemoveRowHandler(
									gridRef,
									MST2020_R01List,
									rowData,
									setClickable,
									removeMST2020_R01List,
									(valueIndex) =>
										addDeletedMST2020_R01List(
											[
												valueIndex.toString(),
											],
										),
								),
						),
				),
				{
					headerName: '거래처코드',
					colId: 'customCd',
					field: 'customCd',
					width: 110,
					editable: (params) =>
						params.data?.inserted === true,
					pasteable: true,
				},
				{
					headerName: '거래처명',
					colId: 'customNm',
					field: 'customNm',
					width: 200,
					editable: false,
				},
				{
					headerName: '매입액',
					colId: 'purchaseAmt',
					field: 'purchaseAmt',
					width: 130,
					editable: true,
					pasteable: true,
					type: 'numericColumn',
					valueFormatter: numberFormatter,
					valueParser: numberParser,
				},
				{
					headerName: '매출액',
					colId: 'salesAmt',
					field: 'salesAmt',
					width: 130,
					editable: true,
					pasteable: true,
					type: 'numericColumn',
					valueFormatter: numberFormatter,
					valueParser: numberParser,
				},
				{
					headerName: '수금액',
					colId: 'receiptAmt',
					field: 'receiptAmt',
					width: 130,
					editable: true,
					pasteable: true,
					type: 'numericColumn',
					valueFormatter: numberFormatter,
					valueParser: numberParser,
				},
				{
					headerName: '지불액',
					colId: 'paymentAmt',
					field: 'paymentAmt',
					width: 130,
					editable: true,
					pasteable: true,
					type: 'numericColumn',
					valueFormatter: numberFormatter,
					valueParser: numberParser,
				},
				{
					headerName: '잔액',
					colId: 'balanceAmt',
					field: 'balanceAmt',
					width: 130,
					editable: false,
					type: 'numericColumn',
					valueGetter: (
						p: ValueGetterParams<MST2020_R01Dto>,
					) =>
						p.data
							? calcBalance(p.data)
							: 0,
					valueFormatter: numberFormatter,
				},
				{
					headerName: '비고',
					colId: 'remark',
					field: 'remark',
					width: 200,
					editable: true,
					pasteable: true,
				},
			],
			[
				deletedMST2020_R01List,
				selectedMST2020_R01List,
				MST2020_R01List,
			],
		);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
		[baseColumnDefs],
	);

	const columnDefs: ColDef<MST2020_R01Dto>[] =
		useMemo(() => {
			if (columnState === null) return baseColumnDefs;

			return GridCustomColumnDefs<MST2020_R01Dto>({
					baseColumnDefs,
					columnState,
					headerNames,
					fallbackHeaderClass:
						aggridHeaderClass,
				});
			},
			[
				baseColumnDefs,
				columnState,
				headerNames,
			],
		);

	const cellDoubleClickActions:
		CustomCellDoubleClickAction<MST2020_R01Dto>[] =
		useMemo(
			() => [
				{
					validate: (field, data) =>
						(
							field === 'customCd'
							|| field === 'customNm'
						)
						&& !!data?.inserted,
					action: (_e, foundData) => {
						detailTableHandlers
							.settingCodeSearchModalProps(
								'거래처',
								foundData.customCd || '',
								false,
								'customCd',
								foundData,
								foundData.valueIndex,
							);
					},
				},
			],
			[detailTableHandlers],
		);

	const handleCellDoubleClick =
		CreateCellDoubleClickHandler(
			MST2020_R01List,
			'valueIndex',
			cellDoubleClickActions,
		);

	const handlePostSort = useCallback(
		(
			params:
				PostSortRowsParams<MST2020_R01Dto>,
		) => {
			postSortHandler(
				params,
				MST2020_R01List,
				originalMST2020_R01List,
				setMST2020_R01List,
			);
		},
		[
			MST2020_R01List,
			originalMST2020_R01List,
			setMST2020_R01List,
		],
	);

	const shiftRangeSelectionConfig = useMemo(
		() => ({
			enableShiftRangeSelect: true,
			idKey: 'valueIndex' as const,
			selectedIds:
				selectedMST2020_R01List as Array<
					string | number
				>,
			setSelectedIds: (
				ids: Array<string | number>,
			) =>
				setSelectedMST2020_R01List(
					ids as string[],
				),
			isRowSelectable: (
				event:
					RowClickedEvent<MST2020_R01Dto>,
			) => {
				if (!clickable) {
					setClickable(true);
					return false;
				}

				return !!event.data;
			},
		}),
		[
			selectedMST2020_R01List,
			setSelectedMST2020_R01List,
			clickable,
			setClickable,
		],
	);

	const pinnedBottomRow = useMemo(() => {
		const sum = (
			key: keyof MST2020_R01Dto,
		) =>
			MST2020_R01List.reduce(
				(acc, row) =>
					acc + Number(row[key] ?? 0),
				0,
			);

		return [
			{
				rowIndex: '',
				customNm:
					`${MST2020_R01List.length.toLocaleString()}건`,
				purchaseAmt: sum('purchaseAmt'),
				salesAmt: sum('salesAmt'),
				receiptAmt: sum('receiptAmt'),
				paymentAmt: sum('paymentAmt'),
				balanceAmt:
					MST2020_R01List.reduce(
						(acc, row) =>
							acc + calcBalance(row),
						0,
					),
				editable: false,
			},
		];
	}, [MST2020_R01List]);

	return (
		<GridContextWrapper<MST2020_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			setPaddedGridData={setMST2020_R01List}
			initSelected={
				initSelectedMST2020_R01List
			}
			defaultRowData={defaultMST2020_R01Row}
			allSelected={
				allSelectedMST2020_R01List
			}
			baseColumnDefs={
				columnState ? columnDefs : baseColumnDefs
			}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<MST2020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={gridDefaultColDef}
				rowClassRules={
					createRowClassRules<MST2020_R01Dto>(
						{
							idKey: 'valueIndex',
							selectedList:
								selectedMST2020_R01List,
							deletedList:
								deletedMST2020_R01List,
						},
					)
				}
				shiftRangeSelection={
					shiftRangeSelectionConfig
				}
				onCellClicked={
					cellEventHandlers.handleCellClick
				}
				onCellDoubleClicked={
					handleCellDoubleClick
				}
				onCellKeyDown={
					cellEventHandlers.handleCellKeyDown
				}
				onCellValueChanged={
					detailTableHandlers.handleCellChange
				}
				onCellEditingStopped={
					detailTableHandlers
						.handleCellEditingStopped
				}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
